import React, { useCallback, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Divider } from 'antd';

import Pficon from 'components/Pficon';
import { SETTINGS_TOOLTIP_TYPES } from 'components/NodeHeaderSettings/constants';
import { QA_ACTION_TYPES } from 'containers/QuestionnairePage/constants';

import ConfirmModal from '../UnconditionalHideAnswer/ConfirmModal';
import HideButton from '../UnconditionalHideAnswer/HideButton';
import RestoreButton from '../UnconditionalHideAnswer/RestoreButton';
import useTooltipTitle from '../UnconditionalHideAnswer/hooks/useTooltipTitle';

import useStatePropsAndActions from './hooks/useStatePropsAndActions';

const UnconditionalHideNode = ({
  nodeRefId,
  nodeType,
  valueType,
  iconHide,
  iconClassesHide,
  iconClassesRestore,
  buttonClasses,
  showHideDivider,
  type = QA_ACTION_TYPES.BUTTON,
}) => {
  const isListItem = type === QA_ACTION_TYPES.BUTTON_WITH_TEXT;
  const {
    showModal,
    actionPayload,
    isNodeHidden,
    featuresEnabled,
    canUnconditionalHideNode,
    hideDividerStyle,
    onToggleNodeVisibility,
  } = useStatePropsAndActions(nodeRefId, nodeType);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const onClick = useCallback(() => {
    if (!showModal) {
      onToggleNodeVisibility(actionPayload);
      return;
    }

    setShowConfirmModal(true);
  }, [showModal, actionPayload, onToggleNodeVisibility]);

  const onConfirm = useCallback(() => {
    onToggleNodeVisibility(actionPayload);
    setShowConfirmModal(false);
  }, [actionPayload, onToggleNodeVisibility]);

  const onCloseConfirmModal = useCallback(() => {
    setShowConfirmModal(false);
  }, []);

  const hideLabel = useTooltipTitle(
    valueType,
    nodeType,
    SETTINGS_TOOLTIP_TYPES.DELETE,
  );
  const restoreLabel = useTooltipTitle(
    valueType,
    nodeType,
    SETTINGS_TOOLTIP_TYPES.RESTORE,
  );

  const confirmModalElement = showConfirmModal ? (
    <ConfirmModal
      nodeType={nodeType}
      valueType={valueType}
      nodeFeatures={featuresEnabled}
      onCancel={onCloseConfirmModal}
      onSubmit={onConfirm}
    />
  ) : null;

  if (isListItem) {
    if (isNodeHidden) {
      return (
        <Fragment>
          {confirmModalElement}
          <Button
            tabIndex="-1"
            className={classNames(
              'text-align-left',
              'border-none',
              'padding-horizontal-xxs',
              'shadow-none',
              'background-transparent',
              'background-transparent-hover',
              'w-full',
              {
                'color-blue-6-hover': canUnconditionalHideNode,
                'color-grey-7 color-grey-7-hover': !canUnconditionalHideNode,
              },
            )}
            disabled={!canUnconditionalHideNode}
            onClick={() => onToggleNodeVisibility(actionPayload)}
          >
            <Pficon
              className={classNames('text-size-secondary', 'margin-right-xxs', {
                'color-grey-7': !canUnconditionalHideNode,
              })}
              type="restart"
            />
            {restoreLabel}
          </Button>
        </Fragment>
      );
    }

    return (
      <Fragment>
        {confirmModalElement}
        <Button
          tabIndex="-1"
          className={classNames(
            'text-align-left',
            'border-none',
            'padding-horizontal-xxs',
            'shadow-none',
            'background-transparent',
            'background-transparent-hover',
            'w-full',
            {
              'color-blue-6-hover': canUnconditionalHideNode,
              'color-grey-7 color-grey-7-hover': !canUnconditionalHideNode,
            },
          )}
          disabled={!canUnconditionalHideNode}
          onClick={onClick}
        >
          <Pficon
            className={classNames('text-size-secondary', 'margin-right-xxs', {
              'color-grey-7': !canUnconditionalHideNode,
            })}
            type={iconHide || 'close'}
          />
          {hideLabel}
        </Button>
      </Fragment>
    );
  }

  return (
    <Fragment>
      {confirmModalElement}
      {isNodeHidden ? (
        <RestoreButton
          nodeType={nodeType}
          valueType={valueType}
          isDisabled={!canUnconditionalHideNode}
          action={() => onToggleNodeVisibility(actionPayload)}
          iconClasses={iconClassesRestore}
          buttonClasses={buttonClasses}
        />
      ) : (
        <Fragment>
          {showHideDivider && (
            <Divider
              type="vertical"
              className="margin-left-tiny margin-right-xxs position-relative"
              style={hideDividerStyle}
            />
          )}
          <HideButton
            nodeType={nodeType}
            valueType={valueType}
            isDisabled={!canUnconditionalHideNode}
            action={onClick}
            icon={iconHide}
            iconClasses={iconClassesHide}
            buttonClasses={buttonClasses}
          />
        </Fragment>
      )}
    </Fragment>
  );
};

UnconditionalHideNode.propTypes = {
  nodeRefId: PropTypes.string.isRequired,
  nodeType: PropTypes.number.isRequired,
  valueType: PropTypes.string.isRequired,
  iconHide: PropTypes.string,
  iconClassesHide: PropTypes.string,
  iconClassesRestore: PropTypes.string,
  buttonClasses: PropTypes.string,
  showHideDivider: PropTypes.bool,
  type: PropTypes.string,
};

export default React.memo(UnconditionalHideNode);
